/**
 * OUTFIT — Skimlinks Test Screen
 * Permet de vérifier que le tracking fonctionne avant la mise en prod
 */
import React, { useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity,
  StyleSheet, SafeAreaView, Linking, Alert,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors, spacing, radius, shadow } from '../theme';
import { buildSkimlinksUrl } from '../services/SkimlinksService';
import { trackClick } from '../services/PurchaseTrackingService';

const PUB_ID = '301454X1789418';

// Produits de test — un par partenaire
const TEST_PRODUCTS = [
  {
    id: 'test_zalando_1',
    partner: 'zalando',
    name: 'Robe midi Zalando',
    brand: 'Only',
    price: 39.99,
    originalUrl: 'https://www.zalando.fr/only-robe-en-jersey-black-on321c1o0-q11.html',
    emoji: '👗',
  },
  {
    id: 'test_asos_1',
    partner: 'asos',
    name: 'Blazer ASOS',
    brand: 'ASOS DESIGN',
    price: 55.00,
    originalUrl: 'https://www.asos.com/fr/asos-design/asos-design-blazer-taille-haute/prd/203438290',
    emoji: '🧥',
  },
  {
    id: 'test_hm_1',
    partner: 'hm',
    name: 'Jean slim H&M',
    brand: 'H&M',
    price: 29.99,
    originalUrl: 'https://www2.hm.com/fr_fr/productpage.1216645027.html',
    emoji: '👖',
  },
  {
    id: 'test_amazon_1',
    partner: 'amazon',
    name: 'Sac à main Amazon',
    brand: 'Amazon Fashion',
    price: 45.00,
    originalUrl: 'https://www.amazon.fr/s?k=sac+main+femme&tag=outfit-21',
    emoji: '👜',
  },
  {
    id: 'test_zara_1',
    partner: 'zara',
    name: 'Top en lin Zara',
    brand: 'Zara',
    price: 22.95,
    originalUrl: 'https://www.zara.com/fr/fr/top-en-lin-p03947043.html',
    emoji: '👚',
  },
];

const PARTNER_COLORS = {
  zalando: '#FF6900',
  asos:    '#111111',
  hm:      '#E50010',
  amazon:  '#FF9900',
  zara:    '#000000',
};

export default function SkimlinksTestScreen({ navigation }) {
  const [results, setResults] = useState({});
  const [clickLog, setClickLog] = useState([]);

  // Générer et afficher l'URL Skimlinks pour un produit
  const generateUrl = (product) => {
    const url = buildSkimlinksUrl(product.originalUrl, {
      userId: 'testuser',
      productId: product.id,
    });
    setResults(prev => ({ ...prev, [product.id]: url }));
  };

  // Simuler un clic et ouvrir dans le navigateur
  const testClick = async (product) => {
    const url = buildSkimlinksUrl(product.originalUrl, {
      userId: 'testuser_' + Date.now(),
      productId: product.id,
    });

    // Logger le clic
    await trackClick({ ...product, affiliateUrl: url });

    // Ajouter au log local
    setClickLog(prev => [{
      time: new Date().toLocaleTimeString('fr-FR'),
      partner: product.partner,
      name: product.name,
      url: url.slice(0, 60) + '...',
    }, ...prev.slice(0, 9)]);

    // Ouvrir dans le navigateur
    Alert.alert(
      'Ouvrir le lien Skimlinks ?',
      `URL trackée :\n${url.slice(0, 80)}...\n\nCe lien enregistre le clic dans Skimlinks (hub.skimlinks.com)`,
      [
        { text: 'Annuler', style: 'cancel' },
        { text: 'Ouvrir', onPress: () => Linking.openURL(url) },
      ]
    );
  };

  // Effacer l'historique des clics
  const clearHistory = async () => {
    await AsyncStorage.removeItem('outfit_click_history');
    setClickLog([]);
    Alert.alert('✓', 'Historique effacé');
  };

  // Voir l'historique complet
  const showHistory = async () => {
    const raw = await AsyncStorage.getItem('outfit_click_history');
    const history = raw ? JSON.parse(raw) : [];
    Alert.alert(
      `Historique (${history.length} clics)`,
      history.slice(0, 5).map(c =>
        `${new Date(c.ts).toLocaleTimeString('fr-FR')} · ${c.partner} · ${c.name.slice(0,20)}`
      ).join('\n') || 'Aucun clic enregistré'
    );
  };

  return (
    <SafeAreaView style={s.safe}>
      <View style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Text style={s.back}>←</Text>
        </TouchableOpacity>
        <Text style={s.title}>TEST SKIMLINKS</Text>
        <View style={{ width: 30 }} />
      </View>
      <View style={s.divider} />

      <ScrollView contentContainerStyle={{ paddingBottom: 40 }}>

        {/* Info */}
        <View style={s.infoCard}>
          <Text style={s.infoTitle}>🔗 Publisher ID : {PUB_ID}</Text>
          <Text style={s.infoDesc}>
            Chaque clic sur un produit génère une URL{' '}
            <Text style={s.code}>go.redirectingat.com</Text> trackée.
            Les conversions apparaissent dans{' '}
            <Text style={s.code}>hub.skimlinks.com</Text> sous 24-48h.
          </Text>
        </View>

        {/* Tests par partenaire */}
        <View style={s.section}>
          <Text style={s.sectionLabel}>TEST PAR PARTENAIRE</Text>
          {TEST_PRODUCTS.map(product => (
            <View key={product.id} style={[s.productCard, shadow.sm]}>
              <View style={s.productTop}>
                <View style={[s.partnerBadge, { backgroundColor: PARTNER_COLORS[product.partner] }]}>
                  <Text style={s.partnerBadgeText}>{product.partner.toUpperCase()}</Text>
                </View>
                <View style={s.productInfo}>
                  <Text style={s.productName}>{product.name}</Text>
                  <Text style={s.productPrice}>{product.price} €</Text>
                </View>
              </View>

              {/* URL générée */}
              {results[product.id] && (
                <View style={s.urlBox}>
                  <Text style={s.urlLabel}>URL Skimlinks :</Text>
                  <Text style={s.urlText} numberOfLines={2}>
                    {results[product.id]}
                  </Text>
                </View>
              )}

              <View style={s.btnRow}>
                <TouchableOpacity style={s.btnSecondary}
                  onPress={() => generateUrl(product)}>
                  <Text style={s.btnSecondaryText}>Générer URL</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[s.btnPrimary, { backgroundColor: PARTNER_COLORS[product.partner] }]}
                  onPress={() => testClick(product)}>
                  <Text style={s.btnPrimaryText}>Tester le clic →</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </View>

        {/* Instructions de vérification */}
        <View style={s.section}>
          <Text style={s.sectionLabel}>COMMENT VÉRIFIER</Text>
          {[
            { step:'1', title:'Cliquer "Tester le clic"', desc:"Une URL go.redirectingat.com est générée et s'ouvre dans le navigateur" },
            { step:'2', title:'Vérifier la redirection', desc:'Le navigateur doit se rediriger vers le bon site partenaire (Zalando, ASOS...)' },
            { step:'3', title:'Vérifier hub.skimlinks.com', desc:'Connectez-vous sur hub.skimlinks.com → Reports → Clicks. Les clics apparaissent sous 30 min' },
            { step:'4', title:'Tester une vraie commande', desc:'Effectue un achat test de <10€ pour vérifier la commission dans Reports → Transactions (24-48h)' },
          ].map(item => (
            <View key={item.step} style={[s.stepCard, shadow.sm]}>
              <View style={s.stepNum}>
                <Text style={s.stepNumText}>{item.step}</Text>
              </View>
              <View style={s.stepInfo}>
                <Text style={s.stepTitle}>{item.title}</Text>
                <Text style={s.stepDesc}>{item.desc}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Log des clics */}
        <View style={s.section}>
          <View style={s.sectionHeader}>
            <Text style={s.sectionLabel}>LOG DES CLICS ({clickLog.length})</Text>
            <View style={s.logBtns}>
              <TouchableOpacity onPress={showHistory}>
                <Text style={s.logBtn}>Historique</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={clearHistory}>
                <Text style={[s.logBtn, { color: '#E53935' }]}>Effacer</Text>
              </TouchableOpacity>
            </View>
          </View>

          {clickLog.length === 0
            ? <View style={s.emptyLog}><Text style={s.emptyLogText}>Aucun clic testé</Text></View>
            : clickLog.map((log, i) => (
              <View key={i} style={[s.logRow, shadow.sm]}>
                <View style={[s.logDot, { backgroundColor: PARTNER_COLORS[log.partner] || '#999' }]} />
                <View style={s.logInfo}>
                  <Text style={s.logName}>{log.name}</Text>
                  <Text style={s.logUrl} numberOfLines={1}>{log.url}</Text>
                </View>
                <Text style={s.logTime}>{log.time}</Text>
              </View>
            ))
          }
        </View>

        {/* Lien hub */}
        <TouchableOpacity style={s.hubBtn}
          onPress={() => Linking.openURL('https://hub.skimlinks.com')}>
          <Text style={s.hubBtnText}>Ouvrir hub.skimlinks.com →</Text>
        </TouchableOpacity>

      </ScrollView>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe:           { flex:1, backgroundColor:'#FFFFFF' },
  header:         { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingHorizontal:spacing.xl, paddingVertical:spacing.md },
  back:           { fontSize:22, color:colors.text.primary, fontWeight:'300' },
  title:          { fontSize:11, letterSpacing:4, fontWeight:'600', color:colors.text.primary },
  divider:        { height:1, backgroundColor:colors.bg.border },

  infoCard:       { margin:spacing.xl, padding:spacing.lg, backgroundColor:'#F0F7FF', borderRadius:radius.md, borderWidth:1, borderColor:'#BBDEFB', gap:8 },
  infoTitle:      { fontSize:13, fontWeight:'600', color:'#1565C0' },
  infoDesc:       { fontSize:12, color:'#1565C0', lineHeight:18 },
  code:           { fontFamily:'monospace', fontSize:11, backgroundColor:'#DDEEFF', paddingHorizontal:4, borderRadius:3 },

  section:        { paddingHorizontal:spacing.xl, marginBottom:spacing.lg },
  sectionHeader:  { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:spacing.sm },
  sectionLabel:   { fontSize:9, letterSpacing:3, fontWeight:'600', color:colors.text.faint, textTransform:'uppercase', marginBottom:spacing.sm },

  productCard:    { backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, padding:spacing.md, marginBottom:10, gap:10 },
  productTop:     { flexDirection:'row', alignItems:'center', gap:12 },
  partnerBadge:   { paddingHorizontal:10, paddingVertical:5, borderRadius:radius.sm },
  partnerBadgeText:{ fontSize:10, fontWeight:'700', color:'#FFFFFF', letterSpacing:1 },
  productInfo:    { flex:1 },
  productName:    { fontSize:13, fontWeight:'500', color:colors.text.primary },
  productPrice:   { fontSize:12, color:colors.text.faint, marginTop:2 },

  urlBox:         { backgroundColor:'#F8F8F8', borderRadius:radius.sm, padding:10, borderWidth:1, borderColor:colors.bg.border },
  urlLabel:       { fontSize:9, letterSpacing:1, color:colors.text.faint, textTransform:'uppercase', marginBottom:4 },
  urlText:        { fontSize:10, fontFamily:'monospace', color:'#1565C0', lineHeight:14 },

  btnRow:         { flexDirection:'row', gap:10 },
  btnSecondary:   { flex:1, paddingVertical:10, borderRadius:radius.sm, borderWidth:1.5, borderColor:colors.bg.border, alignItems:'center' },
  btnSecondaryText:{ fontSize:12, fontWeight:'500', color:colors.text.secondary },
  btnPrimary:     { flex:1, paddingVertical:10, borderRadius:radius.sm, alignItems:'center' },
  btnPrimaryText: { fontSize:12, fontWeight:'600', color:'#FFFFFF' },

  stepCard:       { flexDirection:'row', alignItems:'flex-start', backgroundColor:'#FFFFFF', borderRadius:radius.md, borderWidth:1, borderColor:colors.bg.border, padding:spacing.md, marginBottom:8, gap:12 },
  stepNum:        { width:28, height:28, borderRadius:14, backgroundColor:colors.text.primary, alignItems:'center', justifyContent:'center' },
  stepNumText:    { fontSize:13, fontWeight:'600', color:'#FFFFFF' },
  stepInfo:       { flex:1 },
  stepTitle:      { fontSize:13, fontWeight:'500', color:colors.text.primary, marginBottom:3 },
  stepDesc:       { fontSize:11, color:colors.text.faint, lineHeight:16 },

  logBtns:        { flexDirection:'row', gap:16 },
  logBtn:         { fontSize:12, color:colors.text.secondary },
  emptyLog:       { padding:spacing.lg, alignItems:'center', borderWidth:1, borderColor:colors.bg.border, borderRadius:radius.md, borderStyle:'dashed' },
  emptyLogText:   { fontSize:12, color:colors.text.faint },
  logRow:         { flexDirection:'row', alignItems:'center', backgroundColor:'#FFFFFF', borderRadius:radius.sm, borderWidth:1, borderColor:colors.bg.border, padding:spacing.sm, marginBottom:6, gap:10 },
  logDot:         { width:8, height:8, borderRadius:4 },
  logInfo:        { flex:1 },
  logName:        { fontSize:12, fontWeight:'500', color:colors.text.primary },
  logUrl:         { fontSize:9, color:colors.text.faint, fontFamily:'monospace', marginTop:2 },
  logTime:        { fontSize:10, color:colors.text.faint },

  hubBtn:         { marginHorizontal:spacing.xl, marginTop:spacing.md, padding:spacing.lg, backgroundColor:colors.text.primary, borderRadius:radius.md, alignItems:'center' },
  hubBtnText:     { fontSize:14, fontWeight:'500', color:'#FFFFFF', letterSpacing:0.5 },
});
